import { useWagmi } from 'libs/wagmi';
import { useCallback } from 'react';
import config from 'config';
import { TradeActionBNStr } from 'libs/sdk';
import { SafeDecimal } from 'libs/safedecimal';
import { QueryKey, useQueryClient, useTradeQuery } from 'libs/queries';
import { useNotifications } from 'hooks/useNotifications';
import { useStore } from 'store';
import { Token } from 'libs/tokens';
import { useRestrictedCountry } from 'hooks/useRestrictedCountry';

const spender = config.ui.useDexAggregator
  ? config.addresses.carbon.aggregator!
  : config.addresses.carbon.carbonController;

type TradeProps = {
  source: Token;
  target: Token;
  tradeActions: TradeActionBNStr[];
  isTradeBySource: boolean;
  sourceInput: string;
  targetInput: string;
  quoteId?: string;
};

interface Props {
  onSuccess?: (hash: string) => any;
}

export const useTradeAction = ({ onSuccess }: Props) => {
  const {
    trade: {
      settings: { slippage, deadline },
    },
  } = useStore();
  const { checkRestriction } = useRestrictedCountry();
  const { dispatchNotification } = useNotifications();
  const cache = useQueryClient();
  const { user } = useWagmi();

  const calcMinReturn = useCallback(
    (amount: string) => {
      const slippageBn = new SafeDecimal(slippage).div(100);
      return new SafeDecimal(1).minus(slippageBn).times(amount).toString();
    },
    [slippage],
  );

  const calcMaxInput = useCallback(
    (amount: string) => {
      const slippageBn = new SafeDecimal(slippage).div(100);
      return new SafeDecimal(1).plus(slippageBn).times(amount).toString();
    },
    [slippage],
  );

  const calcDeadline = useCallback((value: string) => {
    const deadlineInMs = new SafeDecimal(value).times(60).times(1000);
    return deadlineInMs.plus(Date.now()).toString();
  }, []);

  const mutation = useTradeQuery();

  const trade = async ({
    source,
    target,
    isTradeBySource,
    sourceInput,
    targetInput,
    tradeActions,
    quoteId,
  }: TradeProps) => {
    const checked = await checkRestriction();
    if (!checked) return;
    if (!user) throw new Error('No user');

    return mutation.mutate(
      {
        source,
        target,
        isTradeBySource,
        tradeActions,
        sourceInput,
        targetInput,
        deadline,
        calcDeadline,
        calcMaxInput,
        calcMinReturn,
        quoteId,
      },
      {
        onSuccess: async (tx) => {
          dispatchNotification('trade', {
            txHash: tx.hash,
            amount: sourceInput,
            from: source.symbol,
            to: target.symbol,
          });

          await tx.wait();
          onSuccess?.(tx.hash);
          const keys = [
            QueryKey.balance(user, source.address),
            QueryKey.balance(user, target.address),
            QueryKey.approval(user, source.address, spender),
          ];
          for (const queryKey of keys) {
            cache.invalidateQueries({ queryKey });
          }
        },
        onError: (e: any) => {
          console.error(e);
        },
      },
    );
  };

  return {
    trade,
    calcMaxInput,
    calcMinReturn,
    calcDeadline,
    isAwaiting: mutation.isPending,
  };
};
